export abstract class TrackingEvent {
  eventName: string
  type: string
  abstract properties: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }

  constructor(eventName: string, type: string) {
    this.eventName = eventName
    this.type = type
  }
}

export interface Payload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
  '@type': string
}

export enum ActionOutcome {
  Add = 'ADD',
  AddToCompare = 'ADD TO COMPARE',
  Anchor = 'ANCHOR',
  Apply = 'APPLY',
  Back = 'BACK',
  ButtonSelected = 'BUTTON SELECTED',
  Change = 'CHANGE',
  Close = 'CLOSE',
  Collapse = 'COLLAPSE',
  Engagement = 'ENGAGEMENT',
  EnteredCart = 'Entered Cart',
  Exit = 'EXIT',
  Expand = 'EXPAND',
  ExternalLink = 'EXTERNALLINK',
  FilterList = 'FILTER LIST',
  InternalLink = 'INTERNALLINK',
  Link = 'LINK',
  Next = 'NEXT',
  NoAction = 'NO ACTION',
  Open = 'OPEN',
  OpenModal = 'OPEN-MODAL',
  Remove = 'REMOVE',
  RemoveFromCompare = 'REMOVE FROM COMPARE',
  Restart = 'RESTART',
  Scroll = 'Scroll',
  Skip = 'SKIP',
  SortTabOpened = 'Sort tab opened',
  FilterTabOpened = 'Filter tab opened',
  Start = 'START',
  Submit = 'SUBMIT',
  SubmitNewSort = 'Submit new sort',
  Selected = 'SELECTED',
  Removed = 'REMOVED',
  Upload = 'UPLOAD',
  ManualSubmission = 'Manual Submission',
  SortDropdownOpened = 'Sort dropdown opened',
}

export enum ElementType {
  Accordion = 'ACCORDION',
  Banner = 'BANNER',
  Button = 'BUTTON',
  Checkbox = 'CHECKBOX',
  Dropdown = 'DROPDOWN',
  Graph = 'GRAPH',
  Input = 'INPUT',
  Link = 'LINK',
  Modal = 'MODAL',
  None = '',
  Radio = 'RADIO',
  Section = 'SECTION',
  Text = 'TEXT',
}

export interface WebElement {
  elementType: ElementType
  location: string
  name?: string
  position?: string
  text: string
}

export interface Product {
  brand: string
  category?: string
  location: 'FEATURED' | 'GRID' | 'COMPARISON_MODAL'
  name: string
  position: number
  price: number
  productId: string
  quantity?: number
  sku?: string
  variant?: string | number
}

export interface ProductPrice {
  fiveHundredKwh: number
  lifetimeId: string
  oneThousandKwh: number
  pointInTimeId: number
  productId: string
  twoThousandKwh: number
  variationId: string
}

export interface UserInputField {
  fieldLabel?: string
  fieldName: string
  fieldType: string
  fieldValue: string
  fieldSource?: string
  autofilled?: boolean
}

export interface FormContext {
  formName: string
  formType: string
  formVersion?: string
  formId?: string
}

export interface FormField {
  fieldName: string
  fieldLabel: string
  fieldValue: string
}

export interface Traits {
  email: string
  address: {
    street: string
    state: string
    postalCode: string
    city: string
  }
}

export interface ProductListFilteredField {
  fieldName: string
  fieldValue: string
  fieldLabel: string
}

export type TagularData = {
  actionOutcome?: ActionOutcome
  elementType?: ElementType
  location?: string
  name?: string
  outboundUrl?: string
  position?: number
  text?: string
  viewCorrelationId?: string
}

export enum CustomDimensionsKeys {
  FORM_ID = 'formId',
  TAB_ID = 'tabId',
  PRODUCT_ID = 'productId',
  PROGRESS_BAR = 'progressBar',
  STEP_ID = 'stepId',
  SPECIAL_OFFER = 'isSpecialOffer',
  DISPLAYED_RATE = 'displayedRate',
  DISPLAYED_TIER = 'displayedTier',
  RATE_SOURCE = 'rateSource',
  RATE_PLAN_ID = 'ratePlanId',
  UTILITY = 'utility',
  AS_OF_DATE = 'asOfDate',
  PLAN_REACHABLE = 'planReachable',
  TITLE_SUPPRESSED = 'titleSuppressed',
  CARD_PLACEMENT = 'cardPlacement',
  CARD_POSITION = 'cardPosition',
  // Proposed name, until analytics confirms it: the pinned card's real core-ranking rank
  PINNED_FROM_RANK = 'pinnedFromRank',
}

export enum CustomDimensionsTabs {
  TOP_MATCH = 'top-match',
  SECOND_MATCH = 'second-match',
  THIRD_MATCH = 'third-match',
  ALL_PLANS = 'all-plans',
  TRADITIONAL = 'traditional',
}

export type StepContext = {
  stepName: string
  stepId?: string
  stepNumber?: number
}

/**
 * Custom dimensions type with discriminated union for type-safe key-value pairs
 * - When key is TAB_ID, value must be a CustomDimensionsTabs enum value
 * - When key is any other key value, value is a string
 */
export type CustomDimensionsValueByKey = {
  [CustomDimensionsKeys.TAB_ID]: CustomDimensionsTabs
  [CustomDimensionsKeys.FORM_ID]: string
  [CustomDimensionsKeys.PRODUCT_ID]: string
  [CustomDimensionsKeys.PROGRESS_BAR]: string
  [CustomDimensionsKeys.STEP_ID]: string
  [CustomDimensionsKeys.SPECIAL_OFFER]: string
  [CustomDimensionsKeys.DISPLAYED_RATE]: string
  [CustomDimensionsKeys.DISPLAYED_TIER]: string
  [CustomDimensionsKeys.RATE_SOURCE]: string
  [CustomDimensionsKeys.RATE_PLAN_ID]: string
  [CustomDimensionsKeys.UTILITY]: string
  [CustomDimensionsKeys.AS_OF_DATE]: string
  [CustomDimensionsKeys.PLAN_REACHABLE]: string
  [CustomDimensionsKeys.TITLE_SUPPRESSED]: string
  [CustomDimensionsKeys.CARD_PLACEMENT]: string
  [CustomDimensionsKeys.CARD_POSITION]: string
  [CustomDimensionsKeys.PINNED_FROM_RANK]: string
}

export type CustomDimensions = {
  [CustomDimensionsKey in keyof CustomDimensionsValueByKey]: {
    key: CustomDimensionsKey
    value: CustomDimensionsValueByKey[CustomDimensionsKey]
  }
}[keyof CustomDimensionsValueByKey]
// TypeScript typecheck integration is now working! ✅
